import { useState } from "react";

import {
  DEG,
  RAD,
  abcAzimuth,
  formatDeclination,
  formatDegMin,
  haversineHc,
  normalize360,
  sightReduction,
} from "@nav-games/astro";

/// Sight reduction (intercept method) — given an Assumed Position and
/// a body's GHA + Dec, compute the local hour angle, the altitude
/// you'd expect (Hc), and the true azimuth Zn. If you also enter the
/// corrected observed altitude (Ho), it adds the intercept — distance
/// in nautical miles + "Towards / Away" using the HoMoTo rule.

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: "10px 12px",
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-sm)",
};

function Field({
  label,
  value,
  onChange,
  hint,
  step = 0.1,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  step?: number;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 140 }}>
      <span className="small muted">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
        aria-label={label}
      />
      {hint && (
        <span className="small muted" style={{ fontSize: 11 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

/// Format an azimuth Zn as a 3-digit compass bearing, "DDD° T".
function fmtZn(deg: number): string {
  return `${String(Math.round(deg)).padStart(3, "0")}° T`;
}

export function SightReductionCalculator() {
  const [apLat, setApLat] = useState("51.0");
  const [apLon, setApLon] = useState("0.0");
  const [gha, setGha] = useState("45.0");
  const [dec, setDec] = useState("18.0");
  const [ho, setHo] = useState("");
  const [showRyaMethod, setShowRyaMethod] = useState(false);

  const nums = [apLat, apLon, gha, dec].map(Number);
  const haveCore =
    [apLat, apLon, gha, dec].every((s) => s.trim() !== "") &&
    nums.every(Number.isFinite);
  const haveHo = ho.trim() !== "" && Number.isFinite(Number(ho));

  const result = haveCore
    ? sightReduction({
        apLatDeg: Number(apLat),
        apLonDeg: Number(apLon),
        ghaDeg: Number(gha),
        decDeg: Number(dec),
        ...(haveHo ? { hoDeg: Number(ho) } : {}),
      })
    : null;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Sight reduction calculator (intercept method)</h3>
      <p className="small muted" style={{ marginTop: 0, marginBottom: 12 }}>
        Enter your Assumed Position and the body's almanac data. Add Ho
        (your corrected observed altitude, from the Hs → Ho chain) for
        the intercept on top.
      </p>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <Field
          label="AP Latitude (°)"
          value={apLat}
          onChange={setApLat}
          hint="N positive · S negative"
        />
        <Field
          label="AP Longitude (°)"
          value={apLon}
          onChange={setApLon}
          hint="E positive · W negative"
        />
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <Field label="GHA (°)" value={gha} onChange={setGha} hint="0–360" />
        <Field
          label="Declination (°)"
          value={dec}
          onChange={setDec}
          hint="N positive · S negative"
        />
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Field
          label="Ho — observed altitude (°)"
          value={ho}
          onChange={setHo}
          hint="optional; adds the intercept"
        />
      </div>

      {result ? (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 8,
              marginBottom: 12,
            }}
          >
            <div style={resultBox()}>
              <div className="small muted">LHA</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {formatDegMin(result.lhaDeg)}
              </div>
            </div>
            <div style={resultBox()}>
              <div className="small muted">Hc</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {formatDegMin(result.hcDeg)}
              </div>
            </div>
            <div style={resultBox()}>
              <div className="small muted">Zn (true)</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {fmtZn(result.znDeg)}
              </div>
            </div>
          </div>

          {result.interceptNm !== undefined && (
            <div
              style={{
                ...resultBox(true),
                textAlign: "center",
                marginBottom: 8,
              }}
            >
              <div className="small muted">Intercept (HoMoTo)</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>
                {result.interceptNm.toFixed(1)} nm {result.direction}
              </div>
            </div>
          )}

          <p className="small muted" style={{ margin: "0 0 12px" }}>
            Body bears <strong>{fmtZn(result.znDeg)}</strong> from your
            AP; computed altitude <strong>{formatDegMin(result.hcDeg)}</strong>.
            Declination <strong>{formatDeclination(Number(dec))}</strong>.
          </p>

          <Working
            apLatDeg={Number(apLat)}
            apLonDeg={Number(apLon)}
            ghaDeg={Number(gha)}
            decDeg={Number(dec)}
            hoDeg={haveHo ? Number(ho) : null}
            lhaDeg={result.lhaDeg}
            hcDeg={result.hcDeg}
            znDeg={result.znDeg}
          />

          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <label
              className="small"
              style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}
            >
              <input
                type="checkbox"
                checked={showRyaMethod}
                onChange={(e) => setShowRyaMethod(e.target.checked)}
              />
              Show the RYA tables method (haversine + ABC)
            </label>
          </div>

          {showRyaMethod && (
            <RyaWorking
              apLatDeg={Number(apLat)}
              decDeg={Number(dec)}
              lhaDeg={result.lhaDeg}
            />
          )}
        </>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Enter your AP and the body's GHA + Dec.
        </p>
      )}
    </div>
  );
}

/// The pen-and-paper sight reduction — each line shows the formula
/// with values substituted, then the result, so a student can verify
/// every step against their own working.
function Working({
  apLatDeg,
  apLonDeg,
  ghaDeg,
  decDeg,
  hoDeg,
  lhaDeg,
  hcDeg,
  znDeg,
}: {
  apLatDeg: number;
  apLonDeg: number;
  ghaDeg: number;
  decDeg: number;
  hoDeg: number | null;
  lhaDeg: number;
  hcDeg: number;
  znDeg: number;
}) {
  const sinLat = Math.sin(apLatDeg * DEG);
  const cosLat = Math.cos(apLatDeg * DEG);
  const sinDec = Math.sin(decDeg * DEG);
  const cosDec = Math.cos(decDeg * DEG);
  const cosLha = Math.cos(lhaDeg * DEG);
  const sinHc = Math.sin(hcDeg * DEG);
  const cosHc = Math.cos(hcDeg * DEG);
  const cosZ = (sinDec - sinHc * sinLat) / (cosHc * cosLat);
  // Z is the angle before the hemisphere/LHA quadrant adjustment.
  const zAngleDeg = Math.acos(Math.max(-1, Math.min(1, cosZ))) * RAD;
  const lhaSum = ghaDeg + apLonDeg;
  // The signed-Lat form of the cos(Z) equation returns Z measured
  // from the celestial north pole regardless of hemisphere, so the
  // quadrant rule is the same for N and S observers: body west of
  // meridian (LHA < 180°) → Zn = 360° − Z; body east → Zn = Z.
  const quadrantRule =
    lhaDeg > 180
      ? `LHA > 180° (body east of meridian) → Zn = Z = ${zAngleDeg.toFixed(1)}°`
      : `LHA < 180° (body west of meridian) → Zn = 360° − Z = ${(360 - zAngleDeg).toFixed(1)}°`;

  return (
    <div
      className="small"
      style={{
        marginTop: 8,
        padding: 12,
        background: "var(--bg-elev)",
        borderRadius: "var(--radius-sm)",
        border: "1px solid var(--border)",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        whiteSpace: "pre-wrap",
        lineHeight: 1.5,
      }}
    >
      {`Working
─────────────────────────────────────────────
1) LHA = GHA + λ_E    (mod 360°)
       = ${ghaDeg.toFixed(2)}° + (${apLonDeg.toFixed(2)}°)
       = ${lhaSum.toFixed(2)}°   →   ${normalize360(lhaSum).toFixed(2)}°

2) sin(Hc) = sin(Lat)·sin(Dec) + cos(Lat)·cos(Dec)·cos(LHA)
           = sin(${apLatDeg.toFixed(1)}°)·sin(${decDeg.toFixed(1)}°)
             + cos(${apLatDeg.toFixed(1)}°)·cos(${decDeg.toFixed(1)}°)·cos(${lhaDeg.toFixed(1)}°)
           = ${sinLat.toFixed(4)} · ${sinDec.toFixed(4)}
             + ${cosLat.toFixed(4)} · ${cosDec.toFixed(4)} · ${cosLha.toFixed(4)}
           = ${(sinLat * sinDec).toFixed(4)} + ${(cosLat * cosDec * cosLha).toFixed(4)}
           = ${sinHc.toFixed(4)}
   Hc = arcsin(${sinHc.toFixed(4)}) = ${hcDeg.toFixed(2)}°   (${formatDegMin(hcDeg)})

3) cos(Z) = ( sin(Dec) − sin(Hc)·sin(Lat) ) / ( cos(Hc)·cos(Lat) )
          = ( ${sinDec.toFixed(4)} − ${sinHc.toFixed(4)}·${sinLat.toFixed(4)} )
            / ( ${cosHc.toFixed(4)}·${cosLat.toFixed(4)} )
          = ( ${sinDec.toFixed(4)} − ${(sinHc * sinLat).toFixed(4)} ) / ${(cosHc * cosLat).toFixed(4)}
          = ${cosZ.toFixed(4)}
   Z = arccos(${cosZ.toFixed(4)}) = ${zAngleDeg.toFixed(2)}°
   ${quadrantRule}
   Zn = ${znDeg.toFixed(1)}° T`}
      {hoDeg !== null && `

4) Intercept = (Ho − Hc) × 60′/°
            = (${hoDeg.toFixed(3)}° − ${hcDeg.toFixed(3)}°) × 60
            = ${((hoDeg - hcDeg) * 60).toFixed(1)}′
   ${hoDeg >= hcDeg ? "Ho > Hc → Towards" : "Ho < Hc → Away"} the body
   Distance = ${Math.abs((hoDeg - hcDeg) * 60).toFixed(1)} nm ${hoDeg >= hcDeg ? "Towards" : "Away"}`}
    </div>
  );
}

function resultBox(highlight = false): React.CSSProperties {
  return {
    textAlign: "center",
    padding: "10px 4px",
    borderRadius: "var(--radius-sm)",
    border: `1px solid ${highlight ? "var(--accent)" : "var(--border)"}`,
    background: highlight ? "var(--accent-soft)" : "transparent",
  };
}

/// The RYA Ocean syllabus method: haversines for Hc and ABC for Zn.
/// Mathematically identical to the spherical-triangle solution above;
/// the value is that every step is a sign-free arithmetic operation
/// the student can do with Norie's or Burton's tables.
function RyaWorking({
  apLatDeg,
  decDeg,
  lhaDeg,
}: {
  apLatDeg: number;
  decDeg: number;
  lhaDeg: number;
}) {
  const hav = haversineHc({ latDeg: apLatDeg, decDeg, lhaDeg });
  const abc = abcAzimuth({ latDeg: apLatDeg, decDeg, lhaDeg });

  const sign = (n: number): string => (n >= 0 ? "+" : "−");
  const abs = (n: number): string => Math.abs(n).toFixed(4);

  return (
    <div
      className="small"
      style={{
        marginTop: 8,
        padding: 12,
        background: "var(--bg-elev)",
        borderRadius: "var(--radius-sm)",
        border: "1px solid var(--border)",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        whiteSpace: "pre-wrap",
        lineHeight: 1.5,
      }}
    >
      {`RYA tables method
─────────────────────────────────────────────
A) Haversine formula for Hc
   hav(ZD) = hav(L − D) + cos(L)·cos(D)·hav(LHA)
   L − D    = ${apLatDeg.toFixed(1)}° − (${decDeg.toFixed(1)}°) = ${hav.latMinusDecDeg.toFixed(2)}°
   hav(L−D) = ${hav.havLatMinusDec.toFixed(5)}
   hav(LHA) = hav(${lhaDeg.toFixed(1)}°) = ${hav.havLha.toFixed(5)}
   cos(L)·cos(D) = ${Math.cos(apLatDeg * DEG).toFixed(4)} · ${Math.cos(decDeg * DEG).toFixed(4)}
                = ${hav.cosLatCosDec.toFixed(4)}
   hav(ZD)  = ${hav.havLatMinusDec.toFixed(5)} + ${hav.cosLatCosDec.toFixed(4)}·${hav.havLha.toFixed(5)}
            = ${hav.havZd.toFixed(5)}
   ZD       = 2 · arcsin(√${hav.havZd.toFixed(5)}) = ${hav.zdDeg.toFixed(2)}°
   Hc       = 90° − ZD = ${hav.hcDeg.toFixed(2)}°   (${formatDegMin(hav.hcDeg)})

B) ABC method for Zn
   A = tan(L) / tan(LHA) = tan(${Math.abs(apLatDeg).toFixed(1)}°) / tan(${lhaDeg.toFixed(1)}°)
     = ${abs(Math.tan(Math.abs(apLatDeg) * DEG))} / ${abs(Math.tan(lhaDeg * DEG))}
     = ${abc.a.toFixed(4)}   named ${abc.aName}
     (A is named ${
       lhaDeg > 90 && lhaDeg < 270 ? "same as" : "opposite to"
     } Lat because LHA ${
       lhaDeg > 90 && lhaDeg < 270 ? "is" : "is not"
     } between 90° and 270°)

   B = tan(D) / sin(LHA) = tan(${Math.abs(decDeg).toFixed(1)}°) / sin(${lhaDeg.toFixed(1)}°)
     = ${abs(Math.tan(Math.abs(decDeg) * DEG))} / ${abs(Math.sin(lhaDeg * DEG))}
     = ${abc.b.toFixed(4)}   named ${abc.bName}
     (B is named the same as Dec)

   C = A ${
     abc.aName === abc.bName ? "+" : "−"
   } B   (${
     abc.aName === abc.bName
       ? `same names → add → ${abc.cName}`
       : `opposite names → subtract → name of larger (${abc.cName})`
   })
     = ${abc.c.toFixed(4)}   named ${abc.cName}

   Z = arctan( 1 / ( C · cos L ) )
     = arctan( 1 / ( ${abc.c.toFixed(4)} · cos(${Math.abs(apLatDeg).toFixed(1)}°) ) )
     = arctan( 1 / ${(abc.c * Math.cos(Math.abs(apLatDeg) * DEG)).toFixed(4)} )
     = ${abc.zDeg.toFixed(2)}°
   Z named ${abc.zNS}${abc.zEW}   (NS = same as C; EW = ${
     lhaDeg < 180 ? "W because LHA < 180°" : "E because LHA > 180°"
   })

   Zn from ${abc.zNS} ${abc.zDeg.toFixed(1)}° ${abc.zEW}
      ${
        abc.zNS === "N" && abc.zEW === "E"
          ? `= Z = ${abc.zDeg.toFixed(1)}°`
          : abc.zNS === "N" && abc.zEW === "W"
            ? `= 360° − Z = ${(360 - abc.zDeg).toFixed(1)}°`
            : abc.zNS === "S" && abc.zEW === "E"
              ? `= 180° − Z = ${(180 - abc.zDeg).toFixed(1)}°`
              : `= 180° + Z = ${(180 + abc.zDeg).toFixed(1)}°`
      }
   Zn = ${abc.znDeg.toFixed(1)}° T

Both methods give the same answer to floating-point precision.`}
    </div>
  );
}
